import { ReactElement, SyntheticEvent, useMemo, useCallback } from 'react';
import {
  Stack,
  DialogContentText,
  Link,
  TextField,
  PopperProps,
  FormControlLabel,
  Switch,
  FormGroup,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVectorSquare as LogisticsZonesIcon } from '@fortawesome/free-solid-svg-icons';
import { useFormikContext } from 'formik';
import moment from 'moment';

import {
  Subscription,
  AccessFeature,
  LogisiticZoneInfo,
  NewFeatureBagde,
} from '~components/index';
import { RoutesActionFilterChip } from '~modules/Routes/styles';
import { hasError } from '~utils/formHelpers';
import { Nullable } from '~globals/types';
import { PlanFeaturesTypes } from '~globals/types/enums';
import { LogisticZoneBase } from '~services/logisticsZones/types';

// eslint-disable-next-line max-len
import { useDialogImportOrdersFromIntegrationContext } from '../../DialogImportOrdersFromIntegrationContext';
// eslint-disable-next-line max-len
import { useDialogImportOrdersFromIntegrationTableContext } from '../../DialogImportOrdersFromIntegrationTableContext';
import { FIELDS_NAME, getInitialValues, INTEGRATION_ID_KEY } from '../../utils';
import { DialogImportOrdersFromIntegrationTable } from '../../styles';

const defaultPickersPoppersProps: Partial<PopperProps> = {
  modifiers: [
    {
      name: 'preventOverflow',
      enabled: true,
      options: {
        altAxis: true,
        altBoundary: true,
        tether: true,
        rootBoundary: 'document',
        padding: 8,
      },
    },
  ],
};

const DialogImportOrdersFromIntegrationPresentation = (): ReactElement => {
  const {
    logisticsZones: { list: logisticsZonesList },
    currentIntegrations: { list: currentIntegrationsList },
  } = useDialogImportOrdersFromIntegrationContext();

  const { columns: tableColumns, selectableProps: tableSelectableProps } =
    useDialogImportOrdersFromIntegrationTableContext();

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    getFieldProps,
  } = useFormikContext<ReturnType<typeof getInitialValues>>();

  const handleChangeDate = useCallback(
    (fieldName: string, resetFieldName?: string) =>
      (value: Date | null): void => {
        setFieldValue(fieldName, value);

        if (resetFieldName) {
          setFieldValue(resetFieldName, null);
        }
      },
    [setFieldValue],
  );

  const handleBlurDate = useCallback(
    (fieldName: string) => (): void => {
      setFieldTouched(fieldName, true, false);
    },
    [setFieldTouched],
  );

  const minDateToField = useMemo(
    () => values[FIELDS_NAME.DATE_FROM] ?? undefined,
    [values],
  );

  const maxDateToField = useMemo(
    () =>
      minDateToField
        ? moment(minDateToField).endOf('day').add(30, 'day').toDate()
        : undefined,
    [minDateToField],
  );

  const defaultCalendarMonthToField = useMemo(() => {
    const currentDate = moment();

    if (!currentDate.isSameOrBefore(maxDateToField)) {
      return maxDateToField ?? undefined;
    }

    const dateTo = values[FIELDS_NAME.DATE_TO] ?? undefined;

    return moment(dateTo).isValid() ? dateTo : undefined;
  }, [values, maxDateToField]);

  const hanldeChangeLogisticsZones = useCallback(
    (_event: SyntheticEvent, value: LogisticZoneBase[]) => {
      let currentValue: Nullable<string[]> = null;

      if (value.length > 0) {
        currentValue = value.map((zone) => zone.zoneId);
      }

      setFieldValue(FIELDS_NAME.ZONES_ID, currentValue);
    },
    [setFieldValue],
  );

  return (
    <Stack spacing={2} flex={1} justifyContent="space-between">
      <DialogContentText color="text.primary">
        Selecciona las integraciones desde las que desees importar tus pedidos
      </DialogContentText>

      <Stack spacing={2}>
        <Stack direction="row" spacing={2}>
          <Subscription.DataRetentionMin.DateTimePicker
            label="Desde"
            value={values[FIELDS_NAME.DATE_FROM]}
            onChange={handleChangeDate(FIELDS_NAME.DATE_FROM)}
            onAccept={handleChangeDate(FIELDS_NAME.DATE_FROM)}
            views={['day', 'hours']}
            PopperProps={defaultPickersPoppersProps}
            disableFuture
            renderInput={(props) => (
              <TextField
                {...props}
                fullWidth
                size="small"
                onBlur={handleBlurDate(FIELDS_NAME.DATE_FROM)}
                error={hasError(touched, errors, FIELDS_NAME.DATE_FROM)}
              />
            )}
          />

          <Subscription.DataRetentionMin.DateTimePicker
            label="Hasta"
            value={values[FIELDS_NAME.DATE_TO]}
            onChange={handleChangeDate(FIELDS_NAME.DATE_TO)}
            onAccept={handleChangeDate(FIELDS_NAME.DATE_TO)}
            views={['day', 'hours']}
            PopperProps={defaultPickersPoppersProps}
            disableFuture
            minDate={minDateToField}
            maxDate={maxDateToField}
            defaultCalendarMonth={defaultCalendarMonthToField}
            renderInput={(props) => (
              <TextField
                {...props}
                fullWidth
                onBlur={handleBlurDate(FIELDS_NAME.DATE_TO)}
                error={hasError(touched, errors, FIELDS_NAME.DATE_TO)}
              />
            )}
          />

          <AccessFeature.AutoComplete
            validFeature={PlanFeaturesTypes.MgmZones}
            multiple
            disableCloseOnSelect
            filterSelectedOptions
            limitTags={1}
            renderTags={(vals, getTagsProps) =>
              vals.map((val, index) => (
                // eslint-disable-next-line react/jsx-key
                <RoutesActionFilterChip
                  icon={
                    val.color ? (
                      <FontAwesomeIcon
                        icon={LogisticsZonesIcon}
                        style={{ color: val.color }}
                      />
                    ) : undefined
                  }
                  label={val.code}
                  size="small"
                  {...getTagsProps({ index })}
                />
              ))
            }
            disableClearable
            options={logisticsZonesList}
            label="Zonas logísticas (opcional)"
            name="zones"
            onChange={hanldeChangeLogisticsZones}
            getOptionLabel={(option) =>
              option.description
                ? `${option.code} ${option.description}`
                : option.code
            }
            renderOption={(props, option) => (
              <li {...props}>
                <LogisiticZoneInfo
                  name={option.code}
                  description={option.description}
                  color={option.color}
                  withTooltip
                />
              </li>
            )}
            fullWidth
          />
        </Stack>

        <Stack direction="row" spacing={2}>
          <FormGroup row sx={{ alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  {...getFieldProps(FIELDS_NAME.ONLY_TODAY_DELIVERY)}
                  checked={values[FIELDS_NAME.ONLY_TODAY_DELIVERY]}
                />
              }
              label="Pedidos a despachar hoy (Solo MercadoLibre)"
              sx={{ margin: 0 }}
            />

            <NewFeatureBagde activeForVersion="1.22.3" />
          </FormGroup>
        </Stack>

        <DialogImportOrdersFromIntegrationTable
          data={currentIntegrationsList}
          columns={tableColumns}
          stickyHeader={{ enabled: true, maxHeight: 310 }}
          selectable={tableSelectableProps}
          extractorKeyValue={INTEGRATION_ID_KEY}
        />
      </Stack>

      <DialogContentText variant="caption">
        Desde acá podrás importar los pedidos de tus integraciones. Para mayor
        información sobre integraciones, diríjase a este{' '}
        <Link
          href="https://routix.tawk.help/article/integraciones-con-otras-plataformas"
          target="_blank"
          rel="noopener noreferrer"
          color="primary"
          underline="hover"
          fontWeight="bold"
        >
          artículo
        </Link>
      </DialogContentText>
    </Stack>
  );
};

export default DialogImportOrdersFromIntegrationPresentation;
